import type { HttpError, HttpErrorRecord } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

import type { UseUploadMutationVariables } from "../services.types"

type UseUploadIntervalUsageOptions = Omit<
  UseMutationOptions<
    unknown,
    HttpError<HttpErrorRecord>,
    UseUploadMutationVariables
  >,
  "mutationFn"
>

const uploadIntervalUsage = async ({
  file,
  organizationId,
}: UseUploadMutationVariables) => {
  try {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("organization_id", organizationId ?? "")

    const response = await httpClient.post(
      "/api/v1/data_manager/interval_usage_uploads",
      formData
    )

    return response
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Uploads a CSV file of interval (AMI) usage data
 *
 * @example
 * const {
 *   error,
 *   isError,
 *   isLoading,
 *   mutate
 * } = useUploadIntervalUsage(options)
 *
 * mutate({ file, organizationId })
 */
export const useUploadIntervalUsage = (
  options: UseUploadIntervalUsageOptions = {}
) => {
  const mutationOptions = options

  const mutation = useMutation({
    ...mutationOptions,
    meta: { ...mutationOptions.meta, handlesOwnErrors: true },
    mutationFn: uploadIntervalUsage,
  })

  return mutation
}
